import {
  Box,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  CloseOutlined,
  ErrorOutline,
  InsertDriveFileOutlined,
} from "@mui/icons-material";

import { fileSize } from "./fileModel";
import type { UploadItem } from "./uploadModel";

function statusText(item: UploadItem): string {
  if (item.status === "uploading") return `Uploading ${item.percent}%`;
  if (item.status === "sent") return "Sent";
  if (item.status === "failed") return item.error ?? "Not sent";
  return "";
}

/** The picked files: name, size and, once the upload starts, where each one is. */
export default function UploadFileList({
  items,
  locked,
  onRemove,
}: {
  items: UploadItem[];
  /** While the upload runs nothing is added or removed. */
  locked: boolean;
  onRemove: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <Box
      component="ul"
      aria-label="Files to upload"
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        maxHeight: { sm: 220 },
        overflowY: "auto",
      }}
    >
      {items.map((item, index) => {
        const status = statusText(item);
        return (
          <Box
            component="li"
            key={item.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 1.25,
              py: 0.75,
              borderTop: index ? "1px solid" : "none",
              borderColor: "divider",
            }}
          >
            {item.status === "sent" ? (
              <CheckCircleOutline
                sx={{ color: "success.main", fontSize: 20 }}
                aria-hidden
              />
            ) : item.status === "failed" ? (
              <ErrorOutline
                sx={{ color: "error.main", fontSize: 20 }}
                aria-hidden
              />
            ) : (
              <InsertDriveFileOutlined
                sx={{ color: "text.secondary", fontSize: 20 }}
                aria-hidden
              />
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap variant="body2" title={item.file.name}>
                {item.file.name}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                color={
                  item.status === "failed" ? "error.main" : "text.secondary"
                }
              >
                {fileSize(item.file.size)}
                {status ? ` - ${status}` : ""}
              </Typography>
              {item.status === "uploading" ? (
                <LinearProgress
                  variant="determinate"
                  value={item.percent}
                  aria-label={`${item.file.name} upload`}
                  sx={{ mt: 0.5, borderRadius: 1 }}
                />
              ) : null}
            </Box>
            <Tooltip title={locked ? "" : "Remove"}>
              <span>
                <IconButton
                  size="small"
                  aria-label={`Remove ${item.file.name}`}
                  disabled={locked}
                  onClick={() => onRemove(item.id)}
                >
                  <CloseOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
}
